import { motion, useScroll, useTransform, useReducedMotion, AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import { ArrowUpRight, ExternalLink, Terminal, X } from 'lucide-react';

const projects = [
  {
    title: 'Felix',
    company: '',
    location: 'Remote',
    category: 'Desktop Application',
    description: 'Turns Kanban tickets into autonomous AI agent pipelines',
    overview: 'A desktop platform where AI agents explore codebases, generate tests, implement features, and open pull requests.',
    features: [
      'AI agents that implement features, write tests, and open PRs from Kanban tickets, letting engineers focus on design and review',
      'Concurrent runtime with isolated git worktrees so multiple agents ship in parallel without merge conflicts',
      'Crash-safe cross-process architecture with JSON-RPC for real-time streaming and staged workflow execution with Claude Agent SDK'
    ],
    technologies: ['Claude Agent SDK', 'Electron', 'Vite', 'SQLite', 'Python', 'TypeScript', 'GitHub'],
    image: '/profile.JPG',
    href: 'https://felix-website-mocha.vercel.app/',
  },
  {
    title: 'Ledger',
    company: '',
    location: 'Remote',
    category: 'Web Application',
    description: 'Debug and Iterate LangGraph Agents',
    overview: 'AI Infrastructure for debugging agents with timeline interface',
    features: [
      'Scrub and replay inspection of agent execution, behavior, and beliefs/contradictions.',
      'Experiment with real-time LLM selection, behavior forking, and prompt tuning to iterate on agent behavior.',
    ],
    technologies: ['Next.js', 'LangGraph', 'FastAPI', 'PostgreSQL', 'Supabase', 'Vercel', 'Python', 'TypeScript', 'Git'],
    image: '/profile.JPG',
    href: 'https://ledger-app-topaz.vercel.app',
  }
];

export function ProjectsSection() {
  const [selectedProject, setSelectedProject] = useState<typeof projects[0] | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ['start start', 'end start'],
  });
  const prefersReduced = useReducedMotion();
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  const factor = prefersReduced || isMobile ? 0.5 : 1;

  const scale = useTransform(scrollYProgress, [0, 1], [1, 1 - 0.1 * factor]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.5, 0]);
  const rotateLeft = useTransform(scrollYProgress, [0, 1], [0, 45 * factor]);
  const rotateRight = useTransform(scrollYProgress, [0, 1], [0, -45 * factor]);

  // Parallax for title
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -100 * factor]);

  return (
      <div ref={wrapperRef} className="relative">
        <section 
          className="min-h-screen sticky top-0 py-16 sm:py-24 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-12 bg-neutral-950 text-[#eceff4] overflow-hidden industrial-grid"
          style={{ zIndex: 3 }}
        >
          <div className="scanline" />
          
          <motion.div style={{ scale, opacity }} className="max-w-7xl mx-auto relative z-10">
            {/* Section label */}
            <div className="absolute -top-12 sm:-top-16 md:-top-20 left-0 flex items-center gap-4">
              <Terminal className="w-4 h-4 text-[#d8dee9]/20" />
              <span className="mono text-xs tracking-[0.4em] text-[#d8dee9]/20">SECTION_02</span>
            </div>

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
              viewport={{ once: true, margin: "-100px" }}
              className="mb-16 sm:mb-20 md:mb-24 lg:mb-32"
              style={{ y: titleY }}
            >
              <h2 
                className="text-[4rem] sm:text-[5rem] md:text-[6rem] lg:text-[8rem] xl:text-[12rem] leading-[0.85] grotesk tracking-tighter"
                style={{ fontFamily: 'var(--font-grotesk)', fontWeight: 700 }}
              >
                Projects
              </h2>
              <div className="flex items-center gap-4 mt-4">
                <div className="w-24 h-px bg-[#eceff4]" />
                <span className="mono text-xs text-[#d8dee9]/40">04 SELECTED WORKS</span>
              </div>
            </motion.div>

            <div className="space-y-0 border-t border-[#d8dee9]/10">
              {projects.map((project, index) => (
                <div key={index}>
                <ProjectItem 
                  project={project} 
                  index={index}
                    isSelected={selectedProject?.title === project.title}
                    onClick={() => setSelectedProject(selectedProject?.title === project.title ? null : project)}
                />
                  <AnimatePresence>
                    {selectedProject?.title === project.title && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                        className="overflow-hidden"
                      >
                        <ExpandedProjectView 
                          project={selectedProject}
                          onClose={() => setSelectedProject(null)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Intense parallax background elements */}
          <motion.div
            style={{ 
              rotate: rotateLeft, 
              y: useTransform(scrollYProgress, [0, 1], [-300 * factor, 600 * factor]),
              opacity: useTransform(scrollYProgress, [0, 1], [0.15, 0])
            }}
            className="absolute top-1/4 right-0 w-[700px] h-[700px] border-2 border-[#d8dee9]/10 translate-x-1/2 pointer-events-none"
          />
          <motion.div
            style={{ 
              rotate: rotateRight, 
              y: useTransform(scrollYProgress, [0, 1], [300 * factor, -600 * factor]),
              opacity: useTransform(scrollYProgress, [0, 1], [0.15, 0])
            }}
            className="absolute bottom-1/4 left-0 w-[600px] h-[600px] border-2 border-[#d8dee9]/10 -translate-x-1/2 pointer-events-none"
          />
          
          {/* Diagonal lines */}
          <motion.div
            style={{ 
              y: useTransform(scrollYProgress, [0, 1], [0, -400 * factor]),
              rotate: 45
            }}
            className="absolute top-0 right-1/3 w-px h-screen bg-[#eceff4]/5 pointer-events-none"
          />
          <motion.div
            style={{ 
              y: useTransform(scrollYProgress, [0, 1], [0, 400 * factor]),
              rotate: -45
            }}
            className="absolute bottom-0 left-1/3 w-px h-screen bg-[#eceff4]/5 pointer-events-none"
          />
        </section>
      </div>
  );
}

function ProjectItem({ 
  project, 
  index,
  isSelected,
  onClick 
}: { 
  project: typeof projects[0]; 
  index: number;
  isSelected?: boolean;
  onClick: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const itemRef = useRef<HTMLButtonElement>(null);
  const prefersReduced = useReducedMotion();
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  const factor = prefersReduced || isMobile ? 0.5 : 1;
  const { scrollYProgress } = useScroll({
    target: itemRef,
    offset: ['start end', 'end start'],
  });

  const x = useTransform(scrollYProgress, [0, 0.5, 1], [index % 2 === 0 ? -50 * factor : 50 * factor, 0, index % 2 === 0 ? 50 * factor : -50 * factor]);

  return (
    <motion.button
      ref={itemRef}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.25, 0.1, 0.25, 1] }}
      viewport={{ once: true, margin: "-50px" }}
      className="group border-b border-[#d8dee9]/10 py-8 sm:py-10 md:py-12 w-full text-left cursor-pointer relative overflow-hidden"
    >
      <motion.div style={{ x }} className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          <div className="lg:col-span-1">
            <div className={`mono text-xs tracking-wider ${
              isSelected ? 'text-[#eceff4]' : 'text-[#d8dee9]/40'
            }`}>
              [{String(index + 1).padStart(2, '0')}]
            </div>
          </div>
          
          <div className="lg:col-span-7">
            <motion.h3 
              className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-2 sm:mb-3 grotesk tracking-tight relative inline-block transition-colors ${
                isSelected ? 'text-[#eceff4]' : ''
              }`}
              style={{ fontFamily: 'var(--font-grotesk)', fontWeight: 700 }}
            >
              {project.title}
              <motion.div
                className="absolute bottom-0 left-0 h-0.5 bg-[#eceff4]"
                initial={{ width: 0 }}
                animate={{ width: isHovered || isSelected ? '100%' : 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.h3>
            <motion.p 
              className={`text-sm mono tracking-wide transition-colors ${
                isSelected ? 'text-[#eceff4]/60' : 'text-neutral-500'
              }`}
            >
              {project.category}
            </motion.p>
          </div>

          <div className="lg:col-span-3">
            <div className="flex items-center gap-4 justify-end">
              <motion.div
                animate={{ 
                  x: isHovered && !isSelected ? 5 : 0,
                  y: isHovered && !isSelected ? -5 : 0
                }}
                transition={{ duration: 0.3 }}
                className="relative w-5 h-5"
              >
                <AnimatePresence mode="wait">
                  {isSelected ? (
                    <motion.div
                      key="close"
                      initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <X className="w-5 h-5 text-[#eceff4]" strokeWidth={1.5} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="arrow"
                      initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <ArrowUpRight className="w-5 h-5 text-[#d8dee9]/40" strokeWidth={1.5} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Hover effect - sliding reveal */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent pointer-events-none"
        initial={{ x: '-100%' }}
        animate={{ x: isHovered ? '100%' : '-100%' }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      />
      
      {/* Edge glow on hover */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#eceff4]"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: isHovered || isSelected ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />
    </motion.button>
  );
}

function ExpandedProjectView({
  project,
  onClose
}: {
  project: typeof projects[0];
  onClose: () => void;
}) {
  return (
    <div className="pt-8 sm:pt-12 md:pt-16 pb-0">
      <div className="space-y-8 sm:space-y-12">
        {/* Project link - matches site CTA pattern */}
        {project.href && (
          <motion.a
            href={project.href}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.4 }}
            className="group relative inline-flex items-center gap-2.5 px-5 py-2.5 text-[#eceff4] border border-[#d8dee9]/20 hover:border-[#d8dee9]/40 transition-all duration-300 overflow-hidden"
          >
            <span className="absolute inset-0 bg-[#eceff4] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
            <span className="mono text-xs tracking-[0.2em] uppercase relative z-10 group-hover:text-black transition-colors duration-300">
              View project
            </span>
            <ExternalLink className="w-3.5 h-3.5 text-[#d8dee9]/60 group-hover:text-black relative z-10 transition-colors duration-300" strokeWidth={1.5} />
          </motion.a>
        )}

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <h4 className="mono text-[10px] tracking-[0.2em] mb-4 text-[#d8dee9]/40">
            Key Features
          </h4>
          <ul className="space-y-3">
            {project.features.map((feature, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: 0.15 + index * 0.05,
                  duration: 0.3,
                }}
                className="text-sm sm:text-base text-[#eceff4]/80 font-light flex items-start gap-3"
              >
                <span className="text-[#d8dee9]/50 mt-0.5 mono text-[10px] flex-shrink-0">
                  [{String(index + 1).padStart(2, '0')}]
                </span>
                <span className="leading-relaxed">{feature}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Tech Stack */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <h4 className="mono text-[10px] tracking-[0.2em] mb-4 text-[#d8dee9]/40">
            Tech Stack
          </h4>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {project.technologies.map((tech, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.35 + index * 0.03,
                  duration: 0.2,
                  type: "spring",
                  stiffness: 200,
                }}
                whileHover={{ 
                  scale: 1.1,
                  y: -3,
                  rotate: 1,
                  transition: { 
                    type: "spring",
                    stiffness: 400,
                    damping: 10
                  }
                }}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white text-black mono text-xs sm:text-sm hover:bg-[#eceff4] cursor-default relative shadow-sm hover:shadow-xl transition-shadow duration-300"
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
